import React, { ReactNode } from "react";
import { Tooltip } from "@mui/material";
import { Delete } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";
import type { MRT_RowData, MRT_TableInstance } from "material-react-table";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import CsvExportButton from "@/components/forms/inputs/button/csvExportButton";
import CrudTableBulkActionButtons from "@/components/tables/toolbar/CrudTableBulkActionButtons";
import type { BulkActionDescriptor } from "@/components/tables/toolbar/bulkActionsSlot";
import { isBulkDeleteEnabled } from "@/components/tables/genericCrudTable.rowVisibility";
import type { EntityAdapter } from "@/components/tables/entityAdapters/entityAdapter";
import { resolveToolbarLayout } from "@/components/tables/toolbar/toolbarLayout";
import styles from "@/components/tables/genericCrudTable.module.css";

type CsvExportProps = React.ComponentProps<typeof CsvExportButton>;

export interface CrudTableTopToolbarProps<TData extends MRT_RowData> {
  table: MRT_TableInstance<TData>;
  entityAdapter?: EntityAdapter;
  subdomain: string;
  readOnlyMode?: boolean;
  dataTestId?: string;
  rightAlignTopToolbarActions?: boolean;
  disableAddItem?: boolean;
  enableRowSelection?: boolean | ((row: any) => boolean);
  enableBulkDelete?: boolean | ((selectedRows: any[]) => boolean);
  getBulkDeleteDisabledTooltip?: (selectedRows: any[]) => string | undefined;
  customTopToolBarAction?: (data?: any) => ReactNode | undefined;
  customTopToolBarActionValue: any;
  customTopToolBarActionButton?: ReactNode;
  additionalCustomTopToolbarElements?: () => ReactNode;
  additionalCustomTopToolbarElementsForRowSelection?: (
    selectedRows: any[]
  ) => ReactNode;
  csvExportUrl?: string | null;
  csvExport: Pick<
    CsvExportProps,
    "additionalFilter" | "additionalSorting" | "columnOrdering" | "handleError"
  >;
  onOpenCreate: () => void;
  bulkDelete: (selectedRows: any[]) => void;
  getBulkActionDescriptors?: (selectedRows: any[]) => BulkActionDescriptor[];
}

const CrudTableTopToolbar = <TData extends MRT_RowData>(
  props: CrudTableTopToolbarProps<TData>
) => {
  const { t } = useTranslation();
  const { table } = props;
  const layout = resolveToolbarLayout(props.entityAdapter);

  const selectedUsers = table
    .getSelectedRowModel()
    .flatRows.map((row) => row.original);
  const hasRowSelection = selectedUsers.length > 0; 
  const noRowsSelected = !hasRowSelection;

  const bulkDeleteDisabledTooltip =
    props.getBulkDeleteDisabledTooltip?.(selectedUsers);
  const isBulkDeleteDisabled = Boolean(bulkDeleteDisabledTooltip);
  const bulkDeleteTooltip = bulkDeleteDisabledTooltip ?? t("delete_selected");

  const topToolbarCustomActionsClassName = props.rightAlignTopToolbarActions
    ? styles.topToolbarCustomActionsRight
    : undefined;

  if (props.readOnlyMode && props.readOnlyMode == true) {
    return (
      <div
        data-testid={props.dataTestId}
        className={topToolbarCustomActionsClassName}
      >
        {layout.showsActionButtonInReadOnlyMode(props.subdomain) &&
          props.customTopToolBarActionButton}
        {props.customTopToolBarAction && (
          <div className={styles.flexContainer}>
            <div className={styles.topToolBarWrapper}>
              {props.customTopToolBarAction(props.customTopToolBarActionValue)}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      data-testid={props.dataTestId}
      className={topToolbarCustomActionsClassName}
    >
      {!layout.hidesCreateArea && (
        <div className={styles.flexContainer}>
          {!props.disableAddItem && (
            <GenericButton
              onClick={() => props.onOpenCreate()}
              data-testid="add-button"
              startIcon={<AddIcon />}
            >
              {t("add")}
            </GenericButton>
          )}

          {/* if enableRowSeletion for a table is set to true only the deleteButton gets rendered  */}
          {props.enableRowSelection &&
            hasRowSelection &&
            isBulkDeleteEnabled(props.enableBulkDelete, selectedUsers) && (
              <Tooltip arrow title={bulkDeleteTooltip} placement="bottom">
                <span
                  className={
                    isBulkDeleteDisabled ? styles.cursorNotAllowed : undefined
                  }
                >
                  <GenericButton
                    color="error"
                    startIcon={<Delete />}
                    disabled={isBulkDeleteDisabled}
                    onClick={() => props.bulkDelete(selectedUsers)}
                  >
                    {t("delete_selected")}
                  </GenericButton>
                </span>
              </Tooltip>
            )}

          {props.getBulkActionDescriptors && hasRowSelection && (
            <CrudTableBulkActionButtons
              actions={props.getBulkActionDescriptors(selectedUsers)}
              noRowsSelected={noRowsSelected}
            />
          )}

          {props.additionalCustomTopToolbarElements &&
            layout.customElementsPosition === "afterBulkActions" &&
            props.additionalCustomTopToolbarElements()}

          {props.customTopToolBarActionButton &&
            props.customTopToolBarActionButton}

          {props.additionalCustomTopToolbarElementsForRowSelection &&
            layout.rowSelectionElementsPosition === "inline" &&
            hasRowSelection &&
            props.additionalCustomTopToolbarElementsForRowSelection(
              selectedUsers
            )}

          {props.csvExportUrl && (
            <CsvExportButton entity={props.csvExportUrl} {...props.csvExport} />
          )}

          {props.customTopToolBarAction &&
            layout.rowSelectionElementsPosition !== "besideCustomAction" && (
            <div className={styles.topToolBarWrapper}>
              {props.customTopToolBarAction(props.customTopToolBarActionValue)}
            </div>
          )}

          {props.customTopToolBarAction &&
            props.additionalCustomTopToolbarElementsForRowSelection &&
            layout.rowSelectionElementsPosition === "besideCustomAction" && (
              <div className={styles.topToolBarWrapper}>
                {props.customTopToolBarAction(
                  props.customTopToolBarActionValue
                )}
                {props.additionalCustomTopToolbarElementsForRowSelection(
                  selectedUsers
                )}
              </div>
            )}

          {props.additionalCustomTopToolbarElements &&
            layout.customElementsPosition === "end" &&
            props.additionalCustomTopToolbarElements()}
        </div>
      )}

      {props.additionalCustomTopToolbarElementsForRowSelection &&
        layout.rowSelectionElementsPosition === "ownRow" && (
          <div className={`${styles.flexContainer} ${styles.marginTopHalfRem}`}>
            {hasRowSelection &&
              props.additionalCustomTopToolbarElementsForRowSelection(
                selectedUsers
              )}
          </div>
        )}
    </div>
  );
};

export default CrudTableTopToolbar;
